import React from 'react';
import contensis from 'Common/api/contensis';
import { Op, OrderBy } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import Metadata from 'Components/Metadata';
import MainLayout from 'Layout/Main';
import Template, { datamap, PageData } from 'Templates/SolentStories/Story';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

const Story: NextPageWithLayout<PageData> = ({ entry, moreStories, ancestors }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    moreStories,
                    ancestors
                })}
            />
        </>
    );
};

Story.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ params, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { story } = params as { story: string };
    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/graduate-stories/${story}`,
            entryLinkDepth: 2,
            entryFields: [
                '*',
                '-facilities',
                '-stories',
                '-financeVideos',
                '-content',
                '-courseBrochure',
                '-entryDescription',
                '-entryThumbnail',
                '-estimatedMinutesRead',
                '-featuredStory',
                '-graduationYear',
                '-levelOfStudy',
                '-relatedCourses',
                '-relatedCoursesLink',
                '-relatedInformationHeading',
                '-workExamples',
                '-email',
                '-entryTitle',
                '-hideEmail',
                '-phoneNumber',
                '-picture',
                '-socialMediaLink',
                '-children',
                '-hasChildren',
                '-alternativeEntry',
                '-assessmentSubheading',
                '-assessmentTypes',
                '-careerSubheading',
                '-careers',
                '-compareEntryRequirements',
                '-compareFeatures',
                '-courseCategory',
                '-courseListingFilterOptions',
                '-currentYear',
                '-degreeShowLink',
                '-enableEmployabilityTab',
                '-entryTitle',
                '-extraInfoCTA',
                '-extraInfoDescription',
                '-extraInfoHeading',
                '-extraInfoImage',
                '-facilitiesHeading',
                '-facilitiesIntroduction',
                '-facilitiesText',
                '-faculty',
                '-feesAndFinanceGraduation',
                '-feesAndFinanceSupport',
                '-graduateRolesList',
                '-graduateRolesSubheading',
                '-heroImage',
                '-heroVideo',
                '-howToApply',
                '-industryCTA',
                '-industryHeading',
                '-industryImage',
                '-industrySalary',
                '-industryText',
                '-industryVideo',
                '-introText',
                '-learnAssessHeading',
                '-learnAssessLink',
                '-learnAssessText',
                '-learningSubheading',
                '-learningTypes',
                '-meetTeamHeading',
                '-openDayLink',
                '-salariesDisclaimer',
                '-school',
                '-solentFuturesCTA',
                '-solentFuturesDescription',
                '-solentFuturesHeading',
                '-solentFuturesImage',
                '-solentFuturesVideo',
                '-staffDisclaimer',
                '-staffDisclaimerIsEnabled',
                '-staffIntroduction',
                '-statBanner',
                '-state',
                '-storiesLink',
                '-studySupportHeading',
                '-studySupportLink',
                '-studySupportText',
                '-studySupportVideo1',
                '-supportHeading',
                '-supportIntroduction',
                '-supportSellingPoint',
                '-supportText',
                '-teachingKeyFact',
                '-variantOptions',
                '-virtualTourLink',
                '-youCouldEarnHeading',
                '-yourFutureHeading',
                '-yourFutureImage',
                '-yourFutureLink',
                ...excludeMetadata
            ]
        });

        const storiesQuery = [
            Op.equalTo('sys.contentTypeId', resPage.entry.sys.contentTypeId),
            Op.in('subject', ...[...new Set([resPage.entry.subject.key])]),
            Op.not(Op.equalTo('sys.id', resPage.entry.sys.id)),
            Op.exists('sys.uri', true)
        ];

        const resStories = await search(storiesQuery, {
            linkDepth: 3,
            pageSize: 4,
            orderBy: OrderBy.desc('sys.version.created'),
            fields: [
                'id',
                'sys',
                'cardImage',
                'listingCardImageDefault',
                'listingCardImageOnHover',
                'student',
                'person',
                'currentRole',
                'company',
                'courseCardQuote',
                'name',
                'position',
                'subject',
                'forename',
                'surname',
                ...excludeMetadata
            ]
        });

        const moreStories = resStories.items;

        if (!resPage.entry) {
            return { error: true };
        }

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        return {
            entry: { ...resPage.entry },
            moreStories,
            ancestors
        };
    });

    if (error) {
        return { notFound: true };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return { props: data };
    }
};

export default Story;
